/**
 * Recovers the ZIP from the free-text `crm__event.address` (RETAIN-1188). German
 * five-digit only, since the suggestion tiering prefix-matches those; undefined
 * otherwise, so callers fall back to the customer's master ZIP.
 */
export function extractZipCodeFromAddress(address: string): string | undefined {
    // `88450 Berkheim` — the ZIP/city pair getFullAddressString writes. Last match wins:
    // stray five-digit numbers live in the company and address lines that precede it.
    const zipBeforeCity = [...address.matchAll(/(?<![\d.])(\d{5})\s+\p{L}/gu)].at(-1);

    if (zipBeforeCity) {
        return zipBeforeCity[1];
    }

    // `10117, Berlin` — comma-separated, so no city letter follows. The lookarounds keep
    // ERP references (`KD.40092`) and longer digit runs out.
    const standaloneZip = [...address.matchAll(/(?<![\d.\p{L}])(\d{5})(?![\d\p{L}])/gu)].at(-1);

    return standaloneZip?.[1];
}

export type ZipAndCity = {
    zipCode: string;
    city?: string;
};

/**
 * The ZIP from {@link extractZipCodeFromAddress}, with the city that follows it, to say where a
 * visit is at a glance. Undefined without a ZIP, so callers fall back to the whole address.
 */
export function extractZipAndCityFromAddress(address: string): ZipAndCity | undefined {
    const zipCode = extractZipCodeFromAddress(address);

    if (!zipCode) {
        return undefined;
    }

    // The city comes right after the ZIP, behind a space or a comma, and runs to the next comma
    const afterZip = address.slice(address.lastIndexOf(zipCode) + zipCode.length);
    const city = /^[\s,]+(\p{L}[^,]*)/u.exec(afterZip)?.[1]?.trim();

    return { zipCode, city };
}
